import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { markSubscribed } from "@/lib/sparkle";
import { trackEvent } from "@/hooks/useArticleAnalytics";

/* NewsLayer Close — Daily Brief + Premium Intelligence (Figma contract). */

export const EntityIntelligenceClose = ({ slug }: { slug: string }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const location = useLocation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.post("/leads/capture", {
        email, source_type: `entity_${slug}_close`, source_page: location.pathname,
        interests: ["daily_brief"], referrer: document.referrer || null, consent: true,
      });
      setDone(true);
      markSubscribed();
      trackEvent("daily_brief_signup", { source: `entity_${slug}_close` });
      toast.success("You're in. The Brief lands tomorrow morning.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-[22px] lg:rounded-[30px] bg-[#06050e] border border-[#291a47] px-[16px] py-[16px] md:px-[24px] md:py-[24px] lg:px-[32px] lg:py-[30px]"
      data-testid="entity-close">
      <div className="absolute -right-[80px] -top-[140px] w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(94,31,242,0.24) 0%, rgba(94,31,242,0) 70%)" }} aria-hidden />
      <div className="relative grid lg:grid-cols-[minmax(0,58fr)_minmax(0,42fr)] gap-[28px] lg:gap-[60px]">
        <div>
          <p className="nl-geist text-[9px] lg:text-[11px] leading-[14px] font-semibold text-[#1fe09c] tracking-[0.02em]">THE DAILY BRIEF</p>
          <h2 className="nl-sg text-[23px] leading-[29px] lg:text-[30px] lg:leading-[38px] font-medium text-[#f6f7fc] mt-[10px]">
            Stay ahead of the onchain economy.
          </h2>
          <p className="nl-geist text-[11px] lg:text-[15px] leading-[14px] lg:leading-[19px] text-[#9499ad] mt-[8px] max-w-[610px]">
            The most important market, protocol and policy intelligence — selected by NewsLayer.
          </p>
          {done ? (
            <p className="nl-geist mt-[26px] h-[46px] lg:h-[54px] flex items-center text-[14px] font-semibold text-[#1fe09c]"
              data-testid="close-subscribed">You're in — the Brief lands tomorrow morning.</p>
          ) : (
            <form onSubmit={submit} className="mt-[26px] flex flex-col sm:flex-row gap-[14px] max-w-[580px]">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" aria-label="Email address"
                className="nl-geist flex-1 h-[46px] lg:h-[54px] rounded-[14px] lg:rounded-[16px] bg-[#050608] border border-[#1f2133] px-[16px] text-[13px] lg:text-[14px] text-white placeholder-[color:var(--nl-text-faint)] outline-none focus:border-[#7333ff] transition-colors duration-200"
                data-testid="close-email-input" />
              <button type="submit" disabled={busy}
                className="nl-geist h-[46px] lg:h-[54px] rounded-[14px] lg:rounded-[16px] bg-[#521ce0] border border-[#7333ff] px-[26px] text-[13px] lg:text-[14px] font-semibold text-[#f6f7fc] hover:bg-[#5f27f0] transition-colors duration-200 disabled:opacity-60"
                data-testid="close-subscribe-button">
                {busy ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          )}
        </div>
        <div className="border-t border-[#1f1433] pt-[22px] lg:border-t-0 lg:pt-0">
          <p className="nl-geist text-[9px] lg:text-[11px] leading-[14px] font-semibold text-[#7a3dff] tracking-[0.02em]">PREMIUM INTELLIGENCE</p>
          <h2 className="nl-sg text-[21px] leading-[27px] lg:text-[28px] lg:leading-[36px] font-medium text-[#f6f7fc] mt-[10px]">
            Deeper context. Fewer blind spots.
          </h2>
          <p className="nl-geist text-[11px] lg:text-[15px] leading-[14px] lg:leading-[19px] text-[#9499ad] mt-[8px] max-w-[480px]">
            Research, intelligence products and advanced entity context built for readers who need more.
          </p>
          <Link to="/premium"
            className="nl-geist inline-block mt-[22px] lg:mt-[34px] text-[10px] lg:text-[12px] leading-[16px] font-semibold text-[#7a3dff] hover:text-white transition-colors duration-200"
            data-testid="close-premium-cta">
            EXPLORE PREMIUM →
          </Link>
        </div>
      </div>
    </div>
  );
};
